import { CircleDotIcon } from "@xola/icons";
import { flash, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredNumberMoreThanZero, requiredString } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { Resource, ResourceType } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { ListingAssignmentModal } from "./ListingAssignmentModal";

const validationSchema = object().shape({
    name: requiredString(),
    count: requiredNumberMoreThanZero(),
    capacity: requiredNumberMoreThanZero(),
});

const initialValues = {
    id: "",
    count: 0,
    name: "",
    capacity: 0,
    type: ResourceType.EQUIPMENT,
    schedules: [],
    seller: "",
    createdBy: { id: "" },
    createdAt: null,
};

interface EditEquipmentProps {
    resource?: Resource;
}

export const EditEquipment = ({ resource }: EditEquipmentProps) => {
    const history = useHistory();
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [displayCapacityPrompt, setDisplayCapacityPrompt] = useState(false);
    const [payload, setPayload] = useState<Resource | null>(null);
    const [localFormValues, setLocalFormValues] = useState<Partial<Resource> | null>(null);
    const equipment = resource ?? initialValues;

    const { mutateAsync: editEquipment, isLoading } = useMutation(async (payload: Resource) => {
        return api.put(`/resources/${equipment?.id}`, payload);
    });

    const handleSubmit = async (values: any) => {
        const localPayload = { ...values, seller: { id: seller.id } };
        if (values.capacity === equipment?.capacity) {
            await submitForm(localPayload);
        } else {
            setPayload(localPayload);
            setDisplayCapacityPrompt(true);
        }
    };

    const handleValueChange = (value: string | number, field: string) => {
        setLocalFormValues({ ...localFormValues, [field]: value });
    };

    const submitForm = async (localPayload = null) => {
        const effectivePayload = payload ?? localPayload;
        if (effectivePayload) {
            try {
                await editEquipment(effectivePayload);
                invalidate("resources", resource?.id);
                flash.success("Resource saved");
            } catch {
                flash.error("Failed to save resource");
            }
        }
    };

    const handleCancel = () => {
        history.push("/resources/equipment");
    };

    const CapacityChangePrompt = () => {
        return (
            <PromptModal
                isOpen={displayCapacityPrompt}
                color="success"
                onConfirm={async () => {
                    setDisplayCapacityPrompt(false);
                    await submitForm();
                }}
                onClose={() => {
                    setDisplayCapacityPrompt(false);
                }}
            >
                <div className="flex flex-col">
                    {payload ? (
                        payload?.capacity > equipment.capacity ? (
                            <IncreaseCapacityContent />
                        ) : (
                            <DecreaseCapacityContent />
                        )
                    ) : null}
                    <p>Are you sure you want to continue?</p>
                </div>
            </PromptModal>
        );
    };

    return (
        <>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={{ ...equipment, ...localFormValues }}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit }) => (
                    <ContentLayout isDisabled={isLoading}>
                        <h3 className="pb-5">Details</h3>
                        <EquipmentFormField name="name" label="Name" placeholder="Name" onChange={handleValueChange} />
                        <EquipmentFormField
                            name="count"
                            label="Number of Units"
                            tooltip="The number of identical equipment items. For example, if you have 3 buses that hold the exact same number of people, set the count to 3."
                            type="number"
                            onChange={handleValueChange}
                        />
                        <EquipmentFormField
                            name="capacity"
                            label="Capacity per Unit"
                            tooltip="The number of spots a single equipment item can accommodate. For example, a bus with 10 seats should have a capacity of 10."
                            type="number"
                            onChange={handleValueChange}
                        />

                        {resource ? <ListingAssignmentModal resource={resource} /> : null}

                        <ContentLayout.Footer>
                            <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" disabled={isLoading} onClick={handleCancel}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                )}
            </XolaFormik>
            <CapacityChangePrompt />
        </>
    );
};

interface EquipmentFormFieldProps {
    name: string;
    label: string;
    tooltip?: string;
    type?: string;
    placeholder?: string;
    onChange: (value: string, name: string) => void;
}

const EquipmentFormField = ({
    name,
    label,
    tooltip,
    type = "text",
    placeholder,
    onChange,
}: EquipmentFormFieldProps) => (
    <FormField name={name} label={label} tooltip={tooltip}>
        <InputField type={type} placeholder={placeholder} onChange={(value) => onChange(value, name)} />
    </FormField>
);

const IncreaseCapacityContent = () => (
    <>
        <p>
            You are about to increase the total capacity of an equipment item that may already be associated with
            events. This action could result in the following:
        </p>
        <span className="flex p-2">
            <CircleDotIcon className="mr-5 flex-shrink-0 self-center" />
            Events that were previously unavailable due to this equipment item may become available.
        </span>
    </>
);

const DecreaseCapacityContent = () => (
    <>
        <p>
            You are about to decrease the total capacity of an equipment item that may already be associated with
            events. This action could result in the following:
        </p>

        <span className="flex p-2">
            <CircleDotIcon size="medium" className="mr-5 flex-shrink-0 self-center" />
            Reduction in availability for events belonging to any product associated with this equipment item.
        </span>
        <span className="flex p-2">
            <CircleDotIcon size="medium" className="mr-5 flex-shrink-0 self-center" />
            Future events associated with this equipment item that already have bookings may become
            &quot;overbooked&quot;, if those events new capacity is less than the quantity of the existing bookings.
        </span>
        <span className="flex p-2">
            <CircleDotIcon size="medium" className="mr-5 flex-shrink-0 self-center" />
            Future events whose capacity has been manually modified associated with this equipment will honor the
            manually adjusted capacity.
        </span>
    </>
);
